import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    Bell,
    Send,
    Plus,
    Paperclip,
    Clock,
    X,
    Megaphone,
    FileText
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';

const NoticeForm: React.FC = () => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [isMom, setIsMom] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [notices, setNotices] = useState<any[]>([]);
    const [isDrafting, setIsDrafting] = useState(false);

    useEffect(() => { fetchNotices(); }, []);

    const fetchNotices = async () => {
        const res = await api.get('/notices');
        setNotices(res.data);
    };

    const handleUpload = async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'notices');
        const res = await api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return res.data.url;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let attachment_url = '';
            if (file) {
                attachment_url = await handleUpload(file);
            }
            await api.post('/notices', { title, content, is_mom: isMom, attachment_url });
            setTitle(''); setContent(''); setFile(null); setIsMom(false);
            setIsDrafting(false);
            fetchNotices();
        } catch (e) { alert('Broadcast failed'); }
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Notices & MoMs" subtitle="Post updates and meeting minutes">
                <button
                    onClick={() => setIsDrafting(!isDrafting)}
                    className="bg-violet-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-violet-700 transition-all text-sm"
                >
                    {isDrafting ? 'Cancel' : 'Compose'}
                    <Plus size={18} className={cn("transition-transform", isDrafting && "rotate-45")} />
                </button>
            </PageHeader>

            {isDrafting && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-in slide-in-from-top-2 duration-300">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="text-xs font-semibold text-slate-500 mb-1 block">Headline</label>
                            <input
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                placeholder="Scheduled Maintenance..."
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-500 mb-1 block">Message</label>
                            <textarea
                                value={content}
                                onChange={e => setContent(e.target.value)}
                                rows={4}
                                placeholder="Details..."
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Attachment</label>
                                <input
                                    type="file"
                                    onChange={e => setFile(e.target.files?.[0] || null)}
                                    className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                                />
                            </div>
                            <div className="flex items-end pb-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isMom}
                                        onChange={e => setIsMom(e.target.checked)}
                                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                    />
                                    <span className="text-sm font-medium text-slate-700">Mark as Meeting Minutes (MoM)</span>
                                </label>
                            </div>
                        </div>
                        <div className="flex justify-end pt-2">
                            <button
                                type="submit"
                                className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all flex items-center gap-2"
                            >
                                Publish <Megaphone size={16} />
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="space-y-4">
                {notices.map((n) => (
                    <div key={n.id} className="bg-white rounded-xl border border-slate-200 p-5 flex justify-between gap-4 hover:border-violet-200 transition-all">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                <span className={cn(
                                    "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                                    n.is_mom ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-blue-50 text-blue-700 border-blue-100"
                                )}>
                                    {n.is_mom ? 'MoM' : 'Notice'}
                                </span>
                                <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                                    <Clock size={10} /> {new Date(n.created_at).toLocaleDateString()}
                                </span>
                            </div>
                            <h4 className="font-bold text-slate-900">{n.title}</h4>
                            <p className="text-slate-500 mt-1 text-sm line-clamp-2">{n.content}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            {n.attachment_url && (
                                <a href={n.attachment_url} target="_blank" rel="noreferrer" className="p-2 bg-slate-50 rounded-lg text-slate-400 hover:text-violet-600">
                                    <Paperclip size={18} />
                                </a>
                            )}
                        </div>
                    </div>
                ))}
                {notices.length === 0 && (
                    <div className="py-12 text-center bg-white rounded-xl border border-dashed border-slate-200">
                        <Bell size={32} className="text-slate-200 mx-auto mb-2" />
                        <p className="text-sm text-slate-400 font-medium">No notices published yet</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NoticeForm;
